/**
 * The rules a results map obeys, shared by the phone and the web chat.
 *
 * Both surfaces draw the same map of the same answer, and the two chats have
 * already drifted apart once (see the note at the top of the `soft-tokens`
 * pair). The three judgements that decide whether the map tells the truth —
 * which rows can be placed, whether there are enough of them to be worth a
 * map, and how far to zoom out — live here so neither app can answer them
 * differently.
 */

/** Anything with a coordinate can be framed; the helpers read nothing else. */
export type MappablePoint = {
  latitude?: number | null;
  longitude?: number | null;
};

/** A placed point: both coordinates present and usable. */
export type PlacedPoint = { latitude: number; longitude: number };

/**
 * A centre and a span in degrees.
 *
 * Deliberately not a `Region` (react-native-maps) or a `LatLngBounds` (Google
 * Maps): this package cannot import either, and both are one line away from
 * this shape.
 */
export type MapFrame = {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
};

/**
 * A lone pin should show its street, not its doorstep.
 *
 * The default suits a tall, full-width map (the phone's). A short letterbox
 * panel wants a smaller floor and a zoom clamp instead: fitting a 0.04-degree
 * box into 180px of height zooms out far enough that six medina restaurants
 * bunch into the middle third of the panel, which is what the web map did
 * before it passed its own value.
 */
const MIN_SPAN_DEG = 0.04;
/** ~50km. Past this the pins stop being a city and start being a country. */
const MAX_SPAN_DEG = 0.45;
/** Breathing room around the framed area. */
const PADDING = 1.4;
/**
 * How far from the median a pin may sit and still get a vote on the frame.
 * Roughly 8km: the city a traveller means when they name one.
 *
 * Day-trip rows ("From Marrakech: Essaouira Excursion") carry their
 * destination's coordinates, not the city's, and one of them would otherwise
 * zoom the map out until every real pin sat on top of its neighbours. Beyond
 * this they stay on the map and simply stop dragging the view.
 *
 * This replaced an 80th-percentile rule that dropped the furthest fifth of the
 * pins. That is reasonable over a hundred venues and wrong over six: on a
 * six-restaurant Marrakech answer it threw away Gueliz, and the pin for it was
 * drawn half off the left edge of the panel. A distance threshold does not
 * care how many results there are.
 *
 * It started at Discover's 25km (`CITY_CORE_SPAN_DEG`) and was measured on
 * production the day it shipped: "best restaurants in marrakech" came back
 * with Agafay and Ourika rows 18-25km out, which at 25km still got a vote and
 * framed the whole region — Tamensourt to Ait Ourir, with all twenty pins in
 * an unreadable clump in the middle. Discover is a full-screen browsing map
 * where that width is the point; a card in a chat thread is not.
 */
const CORE_SPAN_DEG = 0.07;

/**
 * Fewer than this and the map reads as a mistake rather than an overview: two
 * pins say less than the two cards already under them.
 */
const MIN_PINS = 3;
/**
 * And it must cover most of the answer. A three-pin map under an answer naming
 * twelve places is not a summary of that answer, it is a quieter way of hiding
 * nine of them.
 */
const MIN_COVERAGE = 0.6;

/**
 * How many pins carry their number.
 *
 * A twenty-pin answer with twenty numbers on a 240px card is a wall of digits.
 * The leading few are the ones the answer argued for and the ones a reader
 * will look for in the cards; the rest stay on the map as plain dots, because
 * dropping them would hide most of the answer — the thing
 * {@link shouldShowResultsMap} exists to refuse.
 */
export const NUMBERED_PINS = 6;

/**
 * Which number each result wears, keyed by slug.
 *
 * Positions are counted over the PLOTTABLE rows, not over every match: a card
 * whose row has no coordinates has no pin, so it must not take a number and
 * push its neighbours out of step with the map. Those cards are simply absent
 * from the map and carry no badge.
 */
export function resultNumbers<T extends MappablePoint & { slug: string }>(
  rows: readonly T[],
): Map<string, number> {
  const out = new Map<string, number>();
  plottableResults(rows).forEach((r, i) => out.set(r.slug, i));
  return out;
}

/**
 * Rows that can actually be pinned.
 *
 * `0, 0` is rejected along with null. It is a real coordinate in the Gulf of
 * Guinea and never a real Moroccan venue, and it is what a half-written
 * address serialises to — which once put a Marrakech restaurant 4,000km off
 * the coast of Ghana.
 */
export function plottableResults<T extends MappablePoint>(rows: readonly T[]): (T & PlacedPoint)[] {
  return rows.filter((r): r is T & PlacedPoint => {
    const { latitude: lat, longitude: lon } = r;
    if (lat == null || lon == null) return false;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
    return lat !== 0 || lon !== 0;
  });
}

/**
 * Whether this answer earns a map at all.
 *
 * Coordinates live on explore rows; bookable tours are sold as a meeting time
 * rather than an address and come back with none. An answer that mixes the two
 * can easily have more places than pins, and a map is only honest when it
 * shows most of what was said.
 */
export function shouldShowResultsMap(rows: readonly MappablePoint[]): boolean {
  if (rows.length === 0) return false;
  const placed = plottableResults(rows).length;
  if (placed < MIN_PINS) return false;
  return placed / rows.length >= MIN_COVERAGE;
}

/**
 * A frame covering the placed rows.
 *
 * Centres on the median coordinate rather than the mean, so a single day trip
 * to Essaouira cannot drag the view out of the medina, and sizes the span to
 * cover every pin within {@link CORE_SPAN_DEG} of that centre, clamped between
 * a street-level floor and a city-wide ceiling. Returns null when nothing can
 * be placed.
 *
 * `minSpanDeg` overrides the floor for a surface whose map is a short
 * letterbox rather than a tall panel. The algorithm above it — the median
 * centre, the percentile span, the outlier robustness — is the part both
 * surfaces must share, and does not move.
 */
export function resultsMapFrame(
  rows: readonly MappablePoint[],
  opts: { minSpanDeg?: number } = {},
): MapFrame | null {
  const pts = plottableResults(rows);
  if (pts.length === 0) return null;

  const latitude = median(pts.map((p) => p.latitude).sort(ascending));
  const longitude = median(pts.map((p) => p.longitude).sort(ascending));

  // The pins close enough to be part of this city rather than a trip out of it.
  const core = pts.filter(
    (p) =>
      Math.abs(p.latitude - latitude) <= CORE_SPAN_DEG &&
      Math.abs(p.longitude - longitude) <= CORE_SPAN_DEG,
  );
  const framed = core.length > 0 ? core : pts;

  const min = opts.minSpanDeg ?? MIN_SPAN_DEG;
  return {
    latitude,
    longitude,
    latitudeDelta: span(framed.map((p) => Math.abs(p.latitude - latitude)), min),
    longitudeDelta: span(framed.map((p) => Math.abs(p.longitude - longitude)), min),
  };
}

function ascending(a: number, b: number): number {
  return a - b;
}

/** Cover every deviation given, with room to breathe, inside the clamps. */
function span(deviations: number[], minSpanDeg: number): number {
  const widest = deviations.reduce((a, b) => Math.max(a, b), 0);
  return clamp(widest * 2 * PADDING, minSpanDeg, MAX_SPAN_DEG);
}

function median(sorted: number[]): number {
  const n = sorted.length;
  const mid = Math.floor(n / 2);
  return n % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
